import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { useAddDonationCampaignMutation, useCampaignAutocomplete } from '../../../../../queries';
import { Button } from '../../../../components-v2/Button';
import { THEME } from '../../../../components-v2/Theme/theme';
import { background, inputBorderColor } from '../../../../styles/colors';
import { TextInput } from '../../../TextInput';
import { Badge } from '../../Badge';
import { styleSheet } from '../../SearchComponent/styles';
import { ClearFieldIcon } from '../../../svgs/icons/ClearFieldIcon';
import { SearchIcon } from '../../../svgs/icons/SearchIcon';

const selectedCampaignStyleSheet = StyleSheet.create({
	inputReplacement: {
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		border: `1px solid ${inputBorderColor}`,
		borderRadius: THEME.common.borderRadius,
		padding: 10,
		background,
		gap: THEME.spacing.xs,
		boxSizing: 'border-box',
	},
	divider: {
		width: 1,
		alignSelf: 'stretch',
		background: inputBorderColor,
		marginLeft: 'auto',
	},
});

export const DonationCampaignSearch = ({
	onSelectDonationCampaign,
	canAdd = true,
	onCreateCampaign,
	initialSearchFragment,
	className,
	dropdownClassName,
	onClear,
	disabled,
	placeholder = 'Search',
	selectedCampaign,
	onClearCampaign,
}: {
	onSelectDonationCampaign: React.Dispatch<React.SetStateAction<Api.IDonationCampaign>>;
	canAdd?: boolean;
	onCreateCampaign?: (campaign: Api.IDonationCampaign) => void;
	initialSearchFragment?: string;
	className?: string;
	dropdownClassName?: string;
	onClear?: () => void;
	disabled?: boolean;
	placeholder?: string;
	selectedCampaign?: Api.IDonationCampaign;
	onClearCampaign?: () => void;
}) => {
	const [isCampaignSearchDropdownOpen, setIsCampaignSearchDropdownOpen] = React.useState(false);
	const [searchFragment, setSearchFragment] = React.useState<string>(initialSearchFragment ?? '');
	const addCampaignMutation = useAddDonationCampaignMutation({
		onSuccess: (result: Api.IDonationCampaign) => {
			onCreateCampaign?.(result);
			setSearchFragment('');
			setIsCampaignSearchDropdownOpen(false);
		},
	});

	const selectDonationCampaign = (donationCampaign: Api.IDonationCampaign) => {
		onSelectDonationCampaign(donationCampaign);
		setSearchFragment('');
		setIsCampaignSearchDropdownOpen(false);
	};

	const updateSearchFragment = (newValue: string) => {
		setSearchFragment(newValue);

		if (newValue) {
			setIsCampaignSearchDropdownOpen(true);
		} else {
			setIsCampaignSearchDropdownOpen(false);
			if (onClear) {
				onClear();
			}
		}
	};

	const createNewCampaign = () => {
		const donationCampaign: Api.IDonationCampaign = {
			name: searchFragment,
		};
		addCampaignMutation.mutate({
			donationCampaign,
		});
	};

	const handleClearCampaign = () => {
		setSearchFragment('');
		onClearCampaign?.();
	};

	const donationCampaignsQuery = useCampaignAutocomplete({
		enabled: !!searchFragment,
		pageSize: 25,
		fragment: searchFragment,
	});

	const donationCampaigns = donationCampaignsQuery?.data?.values ?? [];

	return (
		<div className={`${css(styleSheet.searchInputWrap)} ${className || ''}`}>
			{selectedCampaign ? (
				<div className={css(selectedCampaignStyleSheet.inputReplacement)}>
					<Badge text={selectedCampaign.name} foregroundColor={selectedCampaign.foregroundColor} />
					{onClearCampaign && (
						<>
							<div className={css(selectedCampaignStyleSheet.divider)} />
							<Button
								type='button'
								variant='ghost'
								size='icon'
								data-testid='clear-campaign-button'
								onClick={handleClearCampaign}
							>
								<ClearFieldIcon />
							</Button>
						</>
					)}
				</div>
			) : (
				<TextInput
					className={css(isCampaignSearchDropdownOpen ? styleSheet.popoverOpen : null)}
					inputId='donation-modal-edit-campaign'
					onChange={e => updateSearchFragment(e.target.value)}
					type='text'
					value={searchFragment}
					placeholder={placeholder}
					leftAccessory={<SearchIcon className={css(styleSheet.searchIcon)} />}
					disabled={disabled}
				/>
			)}

			{isCampaignSearchDropdownOpen ? (
				<div className={`${css(styleSheet.dropdown)} ${dropdownClassName || ''}`}>
					{donationCampaigns.length === 0 && !donationCampaignsQuery.isPending ? <i>No matches found</i> : null}

					{donationCampaigns.map(donationCampaign => {
						return (
							<button
								type='button'
								onClick={() => selectDonationCampaign(donationCampaign)}
								key={`campaign-${donationCampaign.id}`}
							>
								<Badge text={donationCampaign.name} foregroundColor={donationCampaign.foregroundColor} />
							</button>
						);
					})}

					{canAdd && !!searchFragment ? (
						<button type='button' className={css(styleSheet.addNewButton)} onClick={createNewCampaign}>
							<span>+ Add &quot;{searchFragment}&quot;</span>
						</button>
					) : null}
				</div>
			) : null}
		</div>
	);
};
